import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, Trophy } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReadingStreak = () => {
  const { isAuthenticated, token } = useAuth();
  const { t } = useLanguage();
  const [streak, setStreak] = useState({ current_streak: 0, best_streak: 0 });

  useEffect(() => {
    if (isAuthenticated) fetchStreak();
  }, [isAuthenticated]);

  const fetchStreak = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/reading-streak`, { headers });
      setStreak(res.data);
    } catch (e) { /* Ignore */ }
  };

  if (!isAuthenticated || streak.current_streak === 0) return null;

  const getMessage = () => {
    if (streak.current_streak >= 7) return t('streak.fire');
    if (streak.current_streak >= 3) return t('streak.congrats');
    if (streak.current_streak === 1) return t('streak.first');
    return t('streak.keep_going');
  };

  return (
    <motion.div
      data-testid="reading-streak"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto"
    >
      <div className="bg-gradient-to-r from-orange-500/10 to-gold/10 border border-orange-300/30 rounded-2xl p-4 flex items-center gap-4">
        <div className="relative">
          <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <Flame className="w-7 h-7 text-orange-500" />
          </div>
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gold text-night-blue text-xs font-bold flex items-center justify-center"
          >
            {streak.current_streak}
          </motion.div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-bold text-foreground">
              {streak.current_streak} {streak.current_streak === 1 ? t('streak.day') : t('streak.days')}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">{getMessage()}</p>
        </div>
        {streak.best_streak > 1 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
            <Trophy className="w-3 h-3 text-gold" />
            <span>{t('streak.best')}: {streak.best_streak}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ReadingStreak;
